/**
 * Report Cache Service
 * Caches GenerateReport API results to reduce API calls
 * Optimized for monthly data with long TTL (1 hour)
 */

import { logger } from '../utils/logger';

interface CachedReportEntry {
  cacheKey: string;
  data: any;
  lastUpdate: number;
  deviceSNs: string[];
  duration: string;
  granularity: string;
}

interface CacheConfig {
  ttlMs: number;  // Time to live in milliseconds
}

// Default TTL configurations for different granularities
const DEFAULT_TTL: Record<string, number> = {
  minute: 5 * 60 * 1000,      // 5 minutes for minute-level data
  hour: 30 * 60 * 1000,       // 30 minutes for hourly data
  day: 60 * 60 * 1000,        // 1 hour for daily data (monthly charts)
  auto: 30 * 60 * 1000,       // 30 minutes default
};

class ReportCacheService {
  private readonly STORAGE_PREFIX = 'report-cache-';
  private readonly MAX_CACHE_ENTRIES = 20;  // Limit number of cached reports
  private readonly MAX_CACHE_AGE = 24 * 60 * 60 * 1000; // 24 hours max
  
  /**
   * Generate cache key from request parameters
   */
  private generateCacheKey(
    deviceSNs: string[],
    duration: string,
    granularity: string,
    utcOffset?: string
  ): string {
    // Sort device SNs for consistent key generation
    // Use hash of device list to keep key short
    const sortedDevices = [...deviceSNs].sort();
    const deviceHash = this.hashString(sortedDevices.join(','));
    const today = new Date().toISOString().split('T')[0]; // Include date for daily invalidation
    // Normalize utcOffset format: +08:00 -> +8:00 for consistent cache keys
    const normalizedOffset = (utcOffset || '+8:00').replace(/\+0(\d):/, '+$1:');
    const key = `${this.STORAGE_PREFIX}${deviceHash}_${duration}_${granularity}_${normalizedOffset}_${today}`;
    console.log(`📦 [ReportCache] Generated key: ${key} for ${deviceSNs.length} devices`);
    return key;
  }
  
  /**
   * Simple hash function for generating short keys
   */
  private hashString(str: string): string {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return Math.abs(hash).toString(36);
  }
  
  /**
   * Get cached report if valid
   */
  getCachedReport(
    deviceSNs: string[],
    duration: string,
    granularity: string,
    utcOffset?: string,
    config?: Partial<CacheConfig>
  ): { data: any; isStale: boolean } | null {
    console.log(`📦 [ReportCache] Checking cache for: ${duration}/${granularity}, ${deviceSNs.length} devices`);
    const cacheKey = this.generateCacheKey(deviceSNs, duration, granularity, utcOffset);
    const cached = localStorage.getItem(cacheKey);
    
    if (!cached) {
      console.log(`📦 [ReportCache] ❌ No cache found for: ${duration}/${granularity}`);
      return null;
    }
    
    console.log(`📦 [ReportCache] ✅ Found cache entry, parsing...`);
    
    try {
      const entry = JSON.parse(cached) as CachedReportEntry;
      const ttl = config?.ttlMs || DEFAULT_TTL[granularity] || DEFAULT_TTL.auto;
      const age = Date.now() - entry.lastUpdate;
      
      // Check if cache is too old (absolute max)
      if (age > this.MAX_CACHE_AGE) {
        console.log(`📦 [ReportCache] Cache expired (${Math.round(age / 1000 / 60)}min old), removing`);
        localStorage.removeItem(cacheKey);
        return null;
      }
      
      // Check if cache is stale but usable
      const isStale = age > ttl;
      
      console.log(`📦 [ReportCache] Cache hit for ${duration}/${granularity}: ${Math.round(age / 1000 / 60)}min old, stale=${isStale}`);
      
      return {
        data: entry.data,
        isStale
      };
    } catch (e) {
      logger.error('📦 [ReportCache] Error parsing cache:', e);
      localStorage.removeItem(cacheKey);
      return null;
    }
  }
  
  /**
   * Save report to cache
   */
  saveReport(
    deviceSNs: string[],
    duration: string,
    granularity: string,
    data: any,
    utcOffset?: string
  ): void {
    const cacheKey = this.generateCacheKey(deviceSNs, duration, granularity, utcOffset);
    
    const entry: CachedReportEntry = {
      cacheKey,
      data,
      lastUpdate: Date.now(),
      deviceSNs,
      duration,
      granularity
    };
    
    try {
      // Clean up old entries before saving
      this.cleanupOldEntries();
      
      localStorage.setItem(cacheKey, JSON.stringify(entry));
      console.log(`📦 [ReportCache] Saved cache for ${duration}/${granularity}`);
    } catch (e) {
      if (e instanceof DOMException && e.name === 'QuotaExceededError') {
        console.warn('📦 [ReportCache] Storage quota exceeded, clearing old caches...');
        this.clearAllCache();
        
        // Retry once
        try {
          localStorage.setItem(cacheKey, JSON.stringify(entry));
          console.log(`📦 [ReportCache] Saved after cleanup`);
        } catch (retryError) {
          console.error('📦 [ReportCache] Failed to save even after cleanup:', retryError);
        }
      } else {
        console.error('📦 [ReportCache] Failed to save:', e);
      }
    }
  }
  
  /**
   * Invalidate cache for specific devices
   */
  invalidateForDevices(deviceSNs: string[]): void {
    const keys = Object.keys(localStorage).filter(k => k.startsWith(this.STORAGE_PREFIX));
    let invalidated = 0;
    
    keys.forEach(key => {
      // Check if any of the device SNs are in the cache key
      const hasDevice = deviceSNs.some(sn => key.includes(sn));
      if (hasDevice) {
        localStorage.removeItem(key);
        invalidated++;
      }
    });
    
    if (invalidated > 0) {
      logger.log(`📦 [ReportCache] Invalidated ${invalidated} cache entries for devices`);
    }
  }
  
  /**
   * Cleanup old entries to prevent storage overflow
   */
  private cleanupOldEntries(): void {
    const keys = Object.keys(localStorage).filter(k => k.startsWith(this.STORAGE_PREFIX));
    
    // If under limit, no cleanup needed
    if (keys.length < this.MAX_CACHE_ENTRIES) {
      return;
    }
    
    // Get all entries with their ages
    const entries: { key: string; age: number }[] = [];
    
    keys.forEach(key => {
      try {
        const data = localStorage.getItem(key);
        if (data) {
          const entry = JSON.parse(data) as CachedReportEntry;
          entries.push({ key, age: Date.now() - entry.lastUpdate });
        }
      } catch (e) {
        // Invalid entry, remove it
        localStorage.removeItem(key);
      }
    });
    
    // Sort by age (oldest first) and remove oldest entries
    entries.sort((a, b) => b.age - a.age);
    const toRemove = entries.slice(0, entries.length - this.MAX_CACHE_ENTRIES + 5); // Keep some buffer
    
    toRemove.forEach(({ key }) => {
      localStorage.removeItem(key);
    });
    
    if (toRemove.length > 0) {
      logger.log(`📦 [ReportCache] Cleaned up ${toRemove.length} old cache entries`);
    }
  }
  
  /**
   * Clear all report cache
   */
  clearAllCache(): void {
    const keys = Object.keys(localStorage).filter(k => k.startsWith(this.STORAGE_PREFIX));
    keys.forEach(key => localStorage.removeItem(key));
    logger.log(`📦 [ReportCache] Cleared ${keys.length} cache entries`);
  }
  
  /**
   * Get cache statistics for debugging
   */
  getStats(): {
    entries: number;
    totalSize: number;
    oldestAge: number | null;
    newestAge: number | null;
  } {
    const keys = Object.keys(localStorage).filter(k => k.startsWith(this.STORAGE_PREFIX));
    let totalSize = 0;
    let oldestAge: number | null = null;
    let newestAge: number | null = null;
    
    keys.forEach(key => {
      const data = localStorage.getItem(key);
      if (data) {
        totalSize += data.length;
        try {
          const entry = JSON.parse(data) as CachedReportEntry;
          const age = Date.now() - entry.lastUpdate;
          if (oldestAge === null || age > oldestAge) oldestAge = age;
          if (newestAge === null || age < newestAge) newestAge = age;
        } catch (e) {}
      }
    });
    
    return {
      entries: keys.length,
      totalSize,
      oldestAge,
      newestAge
    };
  }
}

// Export singleton instance
export const reportCacheService = new ReportCacheService();
export default reportCacheService;
